import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { listenPlatformConfig, saveDispatchEngineConfig } from '../services/firebase';
import {
  FIELDS, PICKUP_KM, STRATEGIES, formFromStored, buildDispatchV2, effectiveForTown,
  summarizeRideDispatch, methodLabel,
} from '../utils/dispatchEngine';

const tsMs = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v.seconds != null) return v.seconds * 1000;
  return 0;
};

const fmtTime = (ms) => (ms
  ? new Date(ms).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  })
  : '—');

// Labels that wrap onto a second line would push their box lower than the others in the row, so every
// label reserves two lines and sits at the bottom, right above its box.
const LABEL_STYLE = { minHeight: '2.6em', display: 'flex', alignItems: 'flex-end' };

const describe = (eff) => {
  const v = eff.values;
  const km = v.maxPickupKm == null ? 'no limit' : `up to ${v.maxPickupKm} km from the pickup`;
  const shape = eff.strategy === 'batched'
    ? `${v.batchSize} Annas first, the rest of a crowded circle ${v.batchHoldSec} s later`
    : `${v.batchSize} Annas per group · ${v.waveHoldSec} s each`;
  return `${methodLabel(eff.strategy)} · ${shape} · ${km}${eff.shadow ? ' · shadow test on' : ''}`;
};

function NumberGrid({ layer, onChange, disabled, placeholderFor }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'start' }}>
      {FIELDS.map((f) => (
        <div key={f.key} className="form-group" style={{ margin: 0 }}>
          <label className="form-label" style={LABEL_STYLE}>{f.label.toUpperCase()}</label>
          <input
            type="number" min={f.min} max={f.max}
            value={layer[f.key]}
            placeholder={placeholderFor(f.key)}
            onChange={(e) => onChange({ [f.key]: e.target.value })}
            disabled={disabled}
          />
          <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>{f.hint} ({f.min}–{f.max})</p>
        </div>
      ))}
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label" style={LABEL_STYLE}>FARTHEST PICKUP (KM)</label>
        <input
          type="number" min={PICKUP_KM.min} max={PICKUP_KM.max} step="0.5"
          value={layer.maxPickupKm}
          placeholder={placeholderFor('maxPickupKm')}
          onChange={(e) => onChange({ maxPickupKm: e.target.value })}
          disabled={disabled}
        />
        <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Nobody farther from the rider than this is called by the groups. Blank = the town’s own radius.
        </p>
      </div>
    </div>
  );
}

export default function DispatchEngine({ rides = [], isSuperAdmin = false }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const touched = useRef(false);

  useEffect(() => listenPlatformConfig((c) => {
    setCfg(c);
    if (!touched.current) setForm(formFromStored(c.dispatchV2));
  }), []);

  const towns = useMemo(() => {
    if (!cfg) return [];
    const list = [{ id: 'default', name: 'Piler (main town)', radiusKm: Number(cfg.serviceRadiusKm) || null }];
    (Array.isArray(cfg.zones) ? cfg.zones : []).forEach((z) => {
      if (z && z.id) list.push({ id: String(z.id), name: z.name || String(z.id), radiusKm: Number(z.radiusKm) || null, inactive: z.active === false });
    });
    Object.keys((cfg.dispatchV2 && cfg.dispatchV2.towns) || {}).forEach((id) => {
      if (!list.some((t) => t.id === id)) list.push({ id, name: id, radiusKm: null, missing: true });
    });
    return list;
  }, [cfg]);

  const saved = useMemo(() => (cfg ? JSON.stringify(buildDispatchV2(formFromStored(cfg.dispatchV2))) : ''), [cfg]);
  const pending = useMemo(() => (form ? buildDispatchV2(form) : null), [form]);
  const dirty = pending ? JSON.stringify(pending) !== saved : false;

  const recent = useMemo(() => [...rides]
    .sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt))
    .slice(0, 12), [rides]);

  if (!cfg || !form) return <div className="muted" style={{ padding: 24 }}>Loading…</div>;

  const edit = (scope, patch) => {
    touched.current = true;
    setForm((f) => (scope === 'global'
      ? { ...f, global: { ...f.global, ...patch } }
      : { ...f, towns: { ...f.towns, [scope]: { ...(f.towns[scope] || {
        strategy: '', shadowV2: '', maxPickupKm: '', ...Object.fromEntries(FIELDS.map((x) => [x.key, ''])),
      }), ...patch } } }));
  };

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Changing dispatch settings requires Super Admin permissions');
      return;
    }
    setSaving(true);
    try {
      await saveDispatchEngineConfig(buildDispatchV2(form));
      touched.current = false;
      toast.success('Saved — applies to new searches within ~10 seconds');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    touched.current = false;
    setForm(formFromStored(cfg.dispatchV2));
  };

  const defaultPlaceholder = (key) => {
    const f = FIELDS.find((x) => x.key === key);
    return f ? `default ${f.dflt}` : 'town radius';
  };
  const townPlaceholder = (key) => {
    const g = form.global[key];
    if (g !== '' && g != null) return `same as all towns (${g})`;
    return defaultPlaceholder(key);
  };

  const globalEff = effectiveForTown(pending, 'default', cfg.serviceRadiusKm);
  const anyNearest = [pending?.strategy, ...Object.values(pending?.towns || {}).map((t) => t.strategy)].includes('nearest');

  return (
    <div style={{ maxWidth: 960 }}>
      <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #f59e0b' }}>
        <div className="card-title">🧭 Dispatch Engine — how Annas are called for a ride</div>
        <p style={{ fontSize: 13, margin: '6px 0' }}>
          <b>“Circles (today)” is the live method:</b> every circle (200 m, 700 m, 2 km, whole town) calls all its Annas at once.
          <b> “Circles in batches” is live too</b>: the same circles, but a crowded circle calls its nearest few first and the
          rest a few seconds later, inside the same circle time. A thin circle behaves exactly as today.
          “Nearest few first” is not built yet; choosing it still runs the circles. Each search is stamped with what it ran,
          so you can see it in the table at the bottom.
        </p>
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          Saved separately from Settings: the Dispatch tuning and Service zones boxes can never overwrite these.
          A change applies to <b>new</b> searches only; a search already running finishes with the settings it started with.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">All towns (the default for every town, including new ones)</div>
        <div className="form-group">
          <label className="form-label">METHOD</label>
          <select
            value={form.global.strategy}
            onChange={(e) => edit('global', { strategy: e.target.value })}
            disabled={!isSuperAdmin}
          >
            {STRATEGIES.map((s) => (
              <option key={s} value={s}>
                {s === 'rings' ? 'Circles (today)'
                  : s === 'batched' ? 'Circles in batches (crowded circles call the nearest few first)'
                    : 'Nearest few first (not active yet — searches still use circles)'}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">SHADOW TEST (SAFE)</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.global.shadowV2 === 'on'}
              onChange={(e) => edit('global', { shadowV2: e.target.checked ? 'on' : '' })}
              disabled={!isSuperAdmin}
            />
            Record what the new method would have done on each search
          </label>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Only writes a note to the server log (counts and distances, no names). It changes nothing for riders or
            Annas. Use it to check a method on real rides before a town is switched to it.
          </p>
        </div>
        <NumberGrid
          layer={form.global}
          onChange={(patch) => edit('global', patch)}
          disabled={!isSuperAdmin}
          placeholderFor={defaultPlaceholder}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Leave a box blank to use the default shown in grey. Piler would use: <b>{describe(globalEff)}</b>
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Per town (optional)</div>
        <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          A town with everything blank uses “All towns”. Fill only what that town should do differently.
          A new town needs nothing here.
        </p>
        {towns.map((t) => {
          const layer = form.towns[t.id] || {
            strategy: '', shadowV2: '', maxPickupKm: '', ...Object.fromEntries(FIELDS.map((x) => [x.key, ''])),
          };
          const eff = effectiveForTown(pending, t.id, t.radiusKm);
          return (
            <details key={t.id} style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 14 }}>
                <b>{t.name}</b>
                {t.inactive ? ' (not live)' : ''}{t.missing ? ' (town no longer in Service zones)' : ''}
                {pending?.towns?.[t.id] ? ' · has its own settings' : ' · uses All towns'}
                <div className="muted" style={{ fontSize: 12 }}>Will use: {describe(eff)}</div>
              </summary>
              <div style={{ marginTop: 10 }}>
                <div className="form-group">
                  <label className="form-label">METHOD FOR THIS TOWN</label>
                  <select
                    value={layer.strategy}
                    onChange={(e) => edit(t.id, { strategy: e.target.value })}
                    disabled={!isSuperAdmin}
                  >
                    <option value="">Same as all towns</option>
                    <option value="rings">Circles (today)</option>
                    <option value="batched">Circles in batches</option>
                    <option value="nearest">Nearest few first (not active yet)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">SHADOW TEST FOR THIS TOWN</label>
                  <select
                    value={layer.shadowV2}
                    onChange={(e) => edit(t.id, { shadowV2: e.target.value })}
                    disabled={!isSuperAdmin}
                  >
                    <option value="">Same as all towns</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                </div>
                <NumberGrid
                  layer={layer}
                  onChange={(patch) => edit(t.id, patch)}
                  disabled={!isSuperAdmin}
                  placeholderFor={townPlaceholder}
                />
              </div>
            </details>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving || !dirty} onClick={save}>
          {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save'}
        </button>
        {dirty && <button type="button" className="btn btn-ghost" onClick={discard}>Discard changes</button>}
        {dirty && <span className="muted" style={{ fontSize: 12 }}>Unsaved changes</span>}
        {anyNearest && <span className="muted" style={{ fontSize: 12 }}>“Nearest few first” is saved for later; it does nothing yet.</span>}
      </div>

      <div className="card">
        <div className="card-title">Recent searches — what each one ran with</div>
        <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Every new search records the method it used and the numbers it started with. “—” means the search began
          before this update.
        </p>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>BOOKED</th><th>TOWN</th><th>STATUS</th><th>RAN WITH</th><th>ASKED FOR</th>
                <th>ANNAS / GROUP</th><th>SECONDS / GROUP</th><th>FARTHEST PICKUP</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={8} className="muted">No rides yet.</td></tr>
              )}
              {recent.map((r) => {
                const s = summarizeRideDispatch(r);
                return (
                  <tr key={r.id}>
                    <td>{fmtTime(tsMs(r.createdAt))}</td>
                    <td>{s.town === 'default' ? 'Piler' : s.town}</td>
                    <td>{r.status || '—'}</td>
                    <td>{s.pinned ? methodLabel(s.used) : '—'}</td>
                    <td>{s.pinned ? methodLabel(s.asked) : '—'}</td>
                    <td>{s.perGroup ?? '—'}</td>
                    <td>{s.groupSeconds ?? '—'}</td>
                    <td>{s.maxPickupKm == null ? '—' : `${s.maxPickupKm} km`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
